import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import type { HandoverConfirmInput, HandoverView } from '../../types/pettyCash';
import { formatAmount, formatDateTime, imprestFigures, parseOptionalAmount, signedAmount, statusLook } from '../../utils/pettyCash';

/**
 * A side of a petty cash handover. The outgoing holder ('from') counts the cash and confirms it with the advances not
 * yet claimed; then the successor ('to') confirms he received it, which completes the handover: the outgoing holder
 * stops being a holder and the successor holds the petty cash.
 */

interface Props {
  open: boolean;
  handover: HandoverView | null;
  side: 'from' | 'to';
  currency: string;
  onClose: () => void;
  onConfirm: (handoverId: number, input: HandoverConfirmInput) => Promise<void>;
}

const Figure: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
    <Typography variant="body2">{value}</Typography>
  </Grid>
);

const shown = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatAmount(n));

const HandoverConfirmDialog: React.FC<Props> = ({ open, handover: h, side, currency, onClose, onConfirm }) => {
  const [cashText, setCashText] = useState('');
  const [advancesText, setAdvancesText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCashText('');
    setAdvancesText('');
    setNote('');
    setError(null);
    setBusy(false);
  }, [open, h?.handoverId, side]);

  if (!h) return null;
  const cash = parseOptionalAmount(cashText);
  const advances = parseOptionalAmount(advancesText);
  // What the outgoing holder should hand over: the float less the claims not yet reimbursed and the advances.
  const expected = imprestFigures({
    floatAmount: h.floatAmount,
    outstanding: h.outstanding ?? 0,
    thisClaim: 0,
    advances: side === 'from' ? advances.value : h.advances,
    cashInHand: side === 'from' ? cash.value : h.cashCounted,
    tolerance: 0,
    mode: 'off',
  });
  const account = `${h.sapAccount}${h.sapAccountName ? ` ${h.sapAccountName}` : ''}${h.auxiliaryCode ? ` · auxiliary ${h.auxiliaryCode}${h.auxiliaryName ? ` ${h.auxiliaryName}` : ''}` : ''}`;

  const confirm = async () => {
    if (side === 'from') {
      if (!cashText.trim() || !cash.ok || cash.value === null) { setError('Type the cash you counted (0 or more).'); return; }
      if (!advances.ok) { setError('The advances are not an amount.'); return; }
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(h.handoverId, side === 'from'
        ? { side, cashCounted: cash.value as number, advances: advances.value, note: note.trim() || null }
        : { side, note: note.trim() || null });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The handover could not be confirmed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="pc-handover-confirm-title">
      <DialogTitle id="pc-handover-confirm-title">
        {side === 'from' ? `Hand over your petty cash to ${h.to.name}` : `Take over the petty cash of ${h.from.name}`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {side === 'from'
              ? `Count the cash you hold and confirm it. ${h.to.name} then confirms receiving it; after that you are no longer a petty cash holder. Your claims keep their history.`
              : `${h.from.name} counted the cash below. Confirm that you received it: you then hold this petty cash, and ${h.from.name} is no longer a holder.`}
          </Typography>
          <Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" component="div">SAP cash account</Typography>
              <Typography variant="body2">{account}</Typography>
            </Grid>
            <Figure label={`Float ${currency}`.trim()} value={shown(h.floatAmount)} />
            <Figure label="Claims not yet reimbursed" value={shown(h.outstanding)} />
            {side === 'to' ? (
              <>
                <Figure label="Advances not yet claimed" value={shown(h.advances)} />
                <Figure label={`Cash counted by ${h.from.name}`} value={<strong>{shown(h.cashCounted)}</strong>} />
                <Figure label="Expected cash" value={shown(expected.expectedCash)} />
                <Figure label="Difference" value={expected.difference === null ? '—' : signedAmount(expected.difference)} />
              </>
            ) : null}
          </Grid>
          </Box>
          {h.outstandingClaims?.length ? (
            <Box>
              <Typography variant="caption" color="text.secondary">Claims not yet reimbursed</Typography>
              <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
                {h.outstandingClaims.map((c) => (
                  <Typography component="li" variant="body2" key={c.claimId}>
                    {c.claimNo || `Claim ${c.claimId}`} · {formatAmount(c.total)} · {statusLook(c.status).label}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ) : null}
          {h.note ? <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Finance note: </Box>{h.note}</Typography> : null}
          {side === 'to' && h.fromNote ? <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>{h.from.name}: </Box>{h.fromNote}</Typography> : null}
          {side === 'to' && h.fromConfirmedAt ? <Typography variant="caption" color="text.secondary">Counted and confirmed {formatDateTime(h.fromConfirmedAt)}</Typography> : null}
          {side === 'from' ? (
            // A Stack, not a spaced Grid: the Grid's negative margins ran the second field past the dialog's edge.
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  id="pc-handover-cash"
                  label={`Cash counted ${currency}`.trim()}
                  value={cashText}
                  onChange={(e) => setCashText(e.target.value.replace(/[^\d.,]/g, '').slice(0, 20))}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!cashText.trim() && !cash.ok}
                  helperText={expected.expectedCash !== null ? `Expected ${formatAmount(expected.expectedCash)}${expected.difference !== null ? ` · difference ${signedAmount(expected.difference)}` : ''}` : 'The cash you hand over'}
                  required
                  fullWidth
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  id="pc-handover-advances"
                  label="Advances and bills not yet claimed"
                  value={advancesText}
                  onChange={(e) => setAdvancesText(e.target.value.replace(/[^\d.,]/g, '').slice(0, 20))}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!advances.ok}
                  helperText="Optional"
                  fullWidth
                />
              </Box>
            </Stack>
          ) : null}
          <TextField
            id="pc-handover-note"
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
            multiline
            minRows={2}
            fullWidth
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Back</Button>
        <Button onClick={() => { void confirm(); }} disabled={busy}>
          {busy ? 'Confirming…' : side === 'from' ? 'Confirm the handover' : 'Confirm and take over'}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default HandoverConfirmDialog;
